import type { EnvironmentId } from "@t3tools/contracts";

import type { ActivityEvent } from "./activitiesTypes";
import { activityDetailLinks, type ActivityDetailRouteLink } from "./activityDetailLinks";

export type ActivityQuickAccessItem =
  | {
      readonly kind: "link";
      readonly key: string;
      readonly link: ActivityDetailRouteLink;
      readonly occurredAt: string;
      readonly sourceEventId: string;
    }
  | {
      readonly kind: "file_label";
      readonly key: string;
      readonly label: string;
      readonly occurredAt: string;
      readonly sourceEventId: string;
    };

function compareByRecency(a: ActivityQuickAccessItem, b: ActivityQuickAccessItem): number {
  return new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime();
}

function linkDedupeKey(link: ActivityDetailRouteLink): string {
  switch (link.id) {
    case "thread":
      return `thread:${link.params?.threadId ?? ""}`;
    case "agent":
      return `agent:${link.params?.agentId ?? ""}`;
    case "workflow":
      return `workflow:${link.params?.id ?? ""}`;
    case "allowance":
      return "allowance:pending";
    case "file":
      return `file:${link.search?.item ?? link.subtitle ?? ""}`;
    default:
      return link.id;
  }
}

function consider(map: Map<string, ActivityQuickAccessItem>, item: ActivityQuickAccessItem): void {
  if (!map.has(item.key)) {
    map.set(item.key, item);
  }
}

export function collectActivitiesQuickAccess(
  events: readonly ActivityEvent[],
  fallbackEnvironmentId: EnvironmentId | null,
  limit = 12,
): readonly ActivityQuickAccessItem[] {
  const byKey = new Map<string, ActivityQuickAccessItem>();
  const newestFirst = [...events].sort(
    (a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime(),
  );

  for (const event of newestFirst) {
    for (const link of activityDetailLinks(event, fallbackEnvironmentId)) {
      consider(byKey, {
        kind: "link",
        key: linkDedupeKey(link),
        link,
        occurredAt: event.occurredAt,
        sourceEventId: event.id,
      });
    }

    const fileLabel = event.refs.fileLabel?.trim();
    if (fileLabel && !event.refs.vaultItemId) {
      consider(byKey, {
        kind: "file_label",
        key: `file-label:${fileLabel}`,
        label: fileLabel,
        occurredAt: event.occurredAt,
        sourceEventId: event.id,
      });
    }
  }

  return [...byKey.values()].sort(compareByRecency).slice(0, limit);
}
